import type { RemoveRiskFromEnterpriseRiskMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  RemoveRiskFromEnterpriseRiskDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

/**
 * Cache-invalidation parity with the Apollo `evictField` set
 * (`risk` / `risk_score` / `enterprise_risk` / `enterprise_risk_score`).
 */
export const useRemoveRiskFromEnterpriseRisk = createMutationHook({
  name: 'removeRiskFromEnterpriseRisk',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.enterpriseRisk.unlink.mutationOptions({ onSuccess }),
  mapInput: (riskId: string) => ({ RiskId: riskId }),
  invalidate: (trpc) => [
    trpc.frontend.enterpriseRisk.register.queryKey(),
    trpc.frontend.enterpriseRisk.getById.queryKey(),
    trpc.frontend.enterpriseRisk.getByTier.queryKey(),
    trpc.frontend.risk.riskById.queryKey(),
    trpc.frontend.risk.register.queryKey(),
  ],
  // Maps the tRPC `{ affectedRows }` response to the GraphQL mutation shape.
  mapResponse: (result: {
    affectedRows: number;
  }): RemoveRiskFromEnterpriseRiskMutation => ({
    removeRiskFromEnterpriseRisk: {
      __typename: 'GenericMutationOutput',
      affected_rows: result.affectedRows,
    },
  }),
  graphqlDocument: RemoveRiskFromEnterpriseRiskDocument,
  graphqlVariables: (riskId: string) => ({ RiskId: riskId }),
  evictFields: [
    'risk',
    'risk_score',
    'enterprise_risk',
    'enterprise_risk_score',
  ],
  refetchQueries: [namedOperations.Query.getRiskById],
  // The risk-detail read is tRPC-only, so its tanstack cache must refresh even
  // when the write ran through the Apollo path.
  invalidateOnGraphql: (trpc) => [trpc.frontend.risk.riskById.queryKey()],
});
